import React, { useCallback, useMemo } from 'react';

import { di } from 'react-magnetic-di';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { AgenticPlanToolInChat } from '@atlassian/agentic-planning/AgenticPlanToolInChat.tsx';
import { MultipleChoiceQuestionsToolInChat } from '@atlassian/agentic-planning/MultipleChoiceQuestionsToolInChat.tsx';
import { validateJiraAdminAgentAction } from '@atlassian/ai-mate-jira-admin-agent-api/validateJiraAdminAgentAction';
import { JiraAdminAgentRenderer } from '@atlassian/ai-mate-jira-admin-agent/JiraAdminAgentRenderer';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { CreateKBCard } from '@atlassian/conversation-assistant-jsm-create-kb-card/create-kb/CreateKBCard';
import {
	ActionKeys,
	ActionPredicateKeys,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererKeyToPayload,
	type ActionRendererPredicateKeyToPayload,
	type ActionRendererPredicateRegistry,
	type ActionRendererRegistry,
	type ActionValidationFailure,
	type ActionWith,
	type GetOpaqueActionRenderer,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import {
	isActionValidationFailureError,
	useActionRenderer as useActionRendererWithRegistry,
} from '@atlassian/conversation-assistant-message-actions-api/use-action-renderer';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { JiraUpdateWorkItemAllFieldTypesActionRenderer } from '@atlassian/jira-update-work-item-all-field-types-action-renderer/JiraUpdateWorkItemAllFieldTypesActionRenderer';
import { JiraUpdateWorkItemAllFieldTypesActionSkeleton } from '@atlassian/jira-update-work-item-all-field-types-action-renderer/JiraUpdateWorkItemAllFieldTypesActionSkeleton';
import { validateJiraUpdateWorkAllFieldTypesAction } from '@atlassian/jira-update-work-item-all-field-types-action-renderer/utils';
import { JiraUpdateWorkItemFieldsActionRenderer } from '@atlassian/jira-update-work-item-fields-action-renderer/JiraUpdateWorkItemFieldsActionRenderer';
import { JiraUpdateWorkItemFieldsActionSkeleton } from '@atlassian/jira-update-work-item-fields-action-renderer/JiraUpdateWorkItemFieldsActionSkeleton';
import { validateJiraUpdateWorkItemFieldsAction } from '@atlassian/jira-update-work-item-fields-action-renderer/utils';

import { jiraPlansActionData } from '../../../common/utils/plan-action-data';
import { AskQuestionCompletedView } from '../../ask-question-completed-view/AskQuestionCompletedView';
import { AskQuestionRendererAsync } from '../../ask-question/async';
import { CreateProjectProgressUpdateActionRenderer } from '../../atlassian-project-status-update-action-renderer/main';
import { CreateProjectProgressUpdateActionSkeleton } from '../../atlassian-project-status-update-action-renderer/skeleton';
import { validateCreateProjectProgressUpdateAction as isCreateProjectProgressUpdateAction } from '../../atlassian-project-status-update-action-renderer/validateCreateProjectProgressUpdateAction';
import { AtlassianTeamUpdateArchiveAction } from '../../atlassian-team-update-archive';
import { CapacityPlanningActionRenderer } from '../../capacity-planning';
import { ConfluenceAifcAdfUpdateAction } from '../../confluence-aifc-adf-update';
import { ConfluenceFolderCreateAction } from '../../confluence-folder-create';
import { ConfluencePageAddLabelAction } from '../../confluence-page-add-label';
import { ConfluencePageCommentAction } from '../../confluence-page-comment';
import { ConfluencePageCreateAction } from '../../confluence-page-create';
import { ConfluencePageMoveAction } from '../../confluence-page-move';
import { ConfluencePageRenameAction } from '../../confluence-page-rename';
import { DeepResearchActions } from '../../deep-research-actions';
import { ExecutePythonRendererAsync } from '../../execute-python/async';
import { FocusAskCreateAction } from '../../focus-ask-create/FocusAskCreateAction';
import { GoogleCalendarEventCreateRendererAsync } from '../../google-calendar-event-create/async';
import { GoogleDocCreateRendererAsync } from '../../google-doc-create/GoogleDocCreateRendererAsync';
import { HamOnboardingHardwareLink } from '../../ham-onboarding-hardware-link/HamOnboardingHardwareLink';
import { HiringManagerToolUIAction } from '../../hiring-manager-tool-action-renderer';
import { ImageSearchActionAsync } from '../../image-search/async';
import { JiraCreateSprintAction } from '../../jira-create-sprint/JiraCreateSprint';
import { JiraSmartIssueCreateAllFieldAction } from '../../jira-create-work-items-all-field-types';
import { JiraEpicsSuggestAction } from '../../jira-epics-suggest';
import { JiraFindIssuesAction } from '../../jira-find-issues';
import { JiraIssueAssignAction } from '../../jira-issue-assign';
import { JiraIssueDeleteAction } from '../../jira-issue-delete';
import { JiraIssueMoveAction } from '../../jira-issue-move';
import { JiraIssueTransitionAction } from '../../jira-issue-transition';
import { JiraMoveToSprintAction } from '../../jira-move-to-sprint/JiraMoveToSprint';
import { AddDependencyAction } from '../../jira-plans-add-dependency';
import { AddProgramBoardAction } from '../../jira-plans-add-program-board';
import { CreateReleaseAction } from '../../jira-plans-create-release';
import { CreateWorkItemAction } from '../../jira-plans-create-work-item';
import { DeleteProgramBoardAction } from '../../jira-plans-delete-program-board';
import { ManageFeaturesAction } from '../../jira-plans-manage-features';
import { RemoveDependencyAction } from '../../jira-plans-remove-dependency';
import { UpdateWorkSourceAction } from '../../jira-plans-update-work-source';
import { JiraStartSprintAction } from '../../jira-start-sprint/JiraStartSprint';
import { JiraVersionCreateAction } from '../../jira-version-create';
import { JiraViewCreateAndConfigureAction } from '../../jira-view-create-and-configure/JiraViewCreateAndConfigureAction';
import { JiraWorkflowEditorUIAction } from '../../jira-workflow-wizard-actions';
import { JsmCreateEmbeddedRequestAction } from '../../jsm-create-embedded-request';
import { JsmCreateQueueAction } from '../../jsm-create-queue-action/JsmCreateQueueAction';
import { JsmCreateQueueGroupAction } from '../../jsm-create-queue-group-action/JsmCreateQueueGroupAction';
import { JsmCreateRequestAction } from '../../jsm-create-request';
import { JsmCreateRequestTypeAction } from '../../jsm-create-request-type-action/JsmCreateRequestTypeAction';
import { JsmJourneyBuilderUIActionAsync } from '../../jsm-journey-builder-actions/async';
import { JsmLivechatAction } from '../../jsm-livechat';
import { JsmSelectHardwareTypesAction } from '../../jsm-select-hardware-types-action/JsmSelectHardwareTypesAction';
import { JsmSolutionComposerPlanAction } from '../../jsm-solution-composer-plan';
import { JsmUpdateQueueAction } from '../../jsm-update-queue-action/JsmUpdateQueueAction';
import { JsmUpdateQueueGroupAction } from '../../jsm-update-queue-group-action/JsmUpdateQueueGroupAction';
import { JsmUpdateRequestTypeAction } from '../../jsm-update-request-type-action/JsmUpdateRequestTypeAction';
import { JsmUpdateRequestTypePortalGroupsAction } from '../../jsm-update-request-type-portal-groups-action/JsmUpdateRequestTypePortalGroupsAction';
import { JsmUpdateRequestTypeWorkflowAction } from '../../jsm-update-request-type-workflow-action/JsmUpdateRequestTypeWorkflowAction';
import { PlanCompleteRendererAsync } from '../../plan-complete/async';
import { RequireInformationUIAction } from '../../require-information-tool';
import { ResumptionRendererAsync } from '../../resumption-ui/ResumptionRendererAsync';
import { ScheduledChatAction } from '../../scheduled-chat/ScheduledChatActionCard';
import { SlackSendChannelMessageRendererAsync } from '../../slack-send-channel-message/SlackSendChannelMessageRendererAsync';
import { StoreRecommendationAction } from '../../store-recommendation';
import { StudioAutomationCreateRuleAction } from '../../studio-automation-create-rule/StudioAutomationCreateRuleAction';
import { StudioAutomationTriggerRuleBuildAction } from '../../studio-automation-trigger-rule-build';
import { StudioCreateAgentRenderer } from '../../studio-create-agent/StudioCreateAgentRenderer';
import { StudioCustomSkillRenderer } from '../../studio-custom-skill/StudioCustomSkillRenderer';
import { StudioLandingPageRedirect } from '../../studio-landing-page-redirect';
import { StudioTriggerSolutionBuildAction } from '../../studio-trigger-solution-build';
import { StudioUpdateAgentDraftAction } from '../../studio-update-agent-draft';
import { UpdateAgentConfigurationAction } from '../../studio-update-agent/update-agent-configuration';
import { StudioWorkspaceUpdateAgentDraftAction } from '../../studio-update-agent/update-draft-agent';

const jiraGatedRegistery: ActionRendererRegistry<ActionRendererKeyToPayload> = {
	[ActionKeys.UpdateWorkItemFields]: {
		renderer: JiraUpdateWorkItemFieldsActionRenderer,
		fallback: JiraUpdateWorkItemFieldsActionSkeleton,
		validate: validateJiraUpdateWorkItemFieldsAction,
		getAdoptedObjects: (action) => [{ objectType: 'work-item', objectId: action.data.issueId }],
	},
	[ActionKeys.CreateProjectProgressUpdate]: {
		renderer: CreateProjectProgressUpdateActionRenderer,
		fallback: CreateProjectProgressUpdateActionSkeleton,
		validate: isCreateProjectProgressUpdateAction,
		getAdoptedObjects: (action) => [{ objectType: 'project', objectId: action.data.projectId }],
	},
	[ActionKeys.UpdateWorkItemAllFieldTypes]: {
		renderer: JiraUpdateWorkItemAllFieldTypesActionRenderer,
		fallback: JiraUpdateWorkItemAllFieldTypesActionSkeleton,
		validate: validateJiraUpdateWorkAllFieldTypesAction,
		getAdoptedObjects: (action) =>
			(action.data.updates ?? []).map((update) => ({
				objectType: 'work-item',
				objectId: update.issueIdOrKey,
			})),
	},
};

const hamOnboardingSetupCompleteEntry = {
	condition: () => fg('itam_ftw'),
	renderer: (props: React.ComponentProps<typeof HamOnboardingHardwareLink>) => {
		di(HamOnboardingHardwareLink);
		return <HamOnboardingHardwareLink {...props} />;
	},
};

type GetRovoToolsResumptionMigrationEnabled = () => boolean;

const defaultGetRovoToolsResumptionMigrationEnabled: GetRovoToolsResumptionMigrationEnabled = () =>
	FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false);

const createRovoToolsResumptionMigrationRegistry = (
	getRovoToolsResumptionMigrationEnabled: GetRovoToolsResumptionMigrationEnabled,
): ActionRendererRegistry<ActionRendererKeyToPayload> => ({
	[ActionKeys.SlackSendChannelMessage]: {
		renderer: SlackSendChannelMessageRendererAsync,
		condition: getRovoToolsResumptionMigrationEnabled,
	},
	[ActionKeys.GoogleCalendarEventCreate]: {
		renderer: GoogleCalendarEventCreateRendererAsync,
		condition: getRovoToolsResumptionMigrationEnabled,
	},
	[ActionKeys.GoogleDocCreate]: {
		renderer: GoogleDocCreateRendererAsync,
		condition: getRovoToolsResumptionMigrationEnabled,
	},
});

const unifiedRegistery: ActionRendererRegistry<ActionRendererKeyToPayload> = {
	// Jira Actions
	[ActionKeys.JiraFindIssues]: {
		renderer: (props) => {
			di(JiraFindIssuesAction);
			return <JiraFindIssuesAction {...props} />;
		},
	},
	[ActionKeys.JiraSmartIssueCreateAllField]: {
		renderer: (props) => {
			di(FlagsProvider, JiraSmartIssueCreateAllFieldAction);
			if (fg('jira-aifc-migrate-action-renderer')) {
				return <JiraSmartIssueCreateAllFieldAction {...props} />;
			}

			// Otherwise defer to the streaming action renderer
			return undefined;
		},
	},
	[ActionKeys.JiraAdminAgent]: {
		renderer: (props) => {
			return <JiraAdminAgentRenderer {...props} />;
		},
		validate: (action) => {
			const payload = validateJiraAdminAgentAction(action.data);
			if (payload) {
				return {
					isValid: true,
					action: action as ActionWith<
						ActionRendererKeyToPayload[typeof ActionKeys.JiraAdminAgent]
					>,
				};
			}
			return {
				isValid: false,
				error: new Error(`Invalid ${action.key || ''} action payload`),
			};
		},
		condition: () => fg('fe_jira_admin_agent_eap'),
	},
	[ActionKeys.JiraIssueTransition]: {
		renderer: JiraIssueTransitionAction,
		getAdoptedObjects: (action) => [{ objectType: 'work-item', objectId: action.data.ari }],
	},
	[ActionKeys.JiraEpicsSuggest]: {
		renderer: JiraEpicsSuggestAction,
	},
	[ActionKeys.JiraIssueDelete]: {
		renderer: JiraIssueDeleteAction,
		getAdoptedObjects: (action) =>
			(action.data.issues ?? []).map((issue) => ({
				objectType: 'work-item',
				objectId: issue.issueKey,
			})),
	},
	[ActionKeys.JiraIssueMove]: {
		renderer: JiraIssueMoveAction,
		getAdoptedObjects: (action) =>
			(action.data.issues ?? []).map((issue) => ({
				objectType: 'work-item',
				objectId: issue.issueKey,
			})),
	},
	[ActionKeys.JiraIssueAssign]: {
		renderer: JiraIssueAssignAction,
		getAdoptedObjects: (action) => [{ objectType: 'work-item', objectId: action.data.ari }],
	},
	[ActionKeys.JiraVersionCreate]: {
		renderer: JiraVersionCreateAction,
	},
	[ActionKeys.JiraViewCreateAndConfigure]: {
		renderer: JiraViewCreateAndConfigureAction,
	},
	[ActionKeys.JiraCreateSprint]: {
		renderer: JiraCreateSprintAction,
	},
	[ActionKeys.JiraStartSprint]: {
		renderer: JiraStartSprintAction,
	},
	[ActionKeys.JiraMoveToSprint]: {
		renderer: JiraMoveToSprintAction,
	},
	[ActionKeys.ManageCapacityPlan]: {
		renderer: (props) => {
			di(FlagsProvider);
			return fg('ai_features_in_icp_m1') ? (
				<CapacityPlanningActionRenderer {...props} />
			) : undefined;
		},
	},
	[ActionKeys.JiraWorkflowEditor]: {
		renderer: JiraWorkflowEditorUIAction,
	},
	// Confluence Actions
	[ActionKeys.ConfluencePageCreate]: {
		renderer: ConfluencePageCreateAction,
	},
	[ActionKeys.ConfluenceFolderCreate]: {
		renderer: ConfluenceFolderCreateAction,
		condition: () => fg('confluence_folder_create_action_custom_ui'),
	},
	[ActionKeys.ConfluencePageComment]: {
		renderer: ConfluencePageCommentAction,
		getAdoptedObjects: (action) => [{ objectType: 'page', objectId: action.data.contentId }],
	},
	[ActionKeys.AifcAdfUpdate]: {
		renderer: (props) =>
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			FeatureGates.checkGate('aifc_adf_update_tool') ? (
				<ConfluenceAifcAdfUpdateAction {...props} />
			) : undefined,
		getAdoptedObjects: (action) => [{ objectType: 'page', objectId: action.data.contentId }],
	},
	[ActionKeys.ConfluencePageAddLabel]: {
		renderer: (props) => {
			di(FlagsProvider);
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			return FeatureGates.getExperimentValue(
				'confluence_add_page_label_rovo_action_custom_ui',
				'isEnabled',
				false,
			) ? (
				<ConfluencePageAddLabelAction {...props} />
			) : undefined;
		},
		getAdoptedObjects: (action) => [{ objectType: 'page', objectId: action.data.id }],
	},
	[ActionKeys.ConfluencePageRename]: {
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		condition: () =>
			FeatureGates.getExperimentValue(
				'confluence_rename_page_rovo_action_custom_ui',
				'isEnabled',
				false,
			),
		renderer: (props) => {
			di(ConfluencePageRenameAction);
			return <ConfluencePageRenameAction {...props} />;
		},
		getAdoptedObjects: (action) => [{ objectType: 'page', objectId: action.data.documentId }],
	},
	[ActionKeys.ConfluencePageMove]: {
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		condition: () =>
			FeatureGates.getExperimentValue(
				'confluence_move_page_rovo_action_custom_ui',
				'isEnabled',
				false,
			),
		renderer: (props) => {
			di(ConfluencePageMoveAction);
			return <ConfluencePageMoveAction {...props} />;
		},
		getAdoptedObjects: (action) => [{ objectType: 'page', objectId: action.data.documentId }],
	},
	[ActionKeys.JsmJourneyBuilder]: {
		renderer: (props) => {
			di(JsmJourneyBuilderUIActionAsync);
			return <JsmJourneyBuilderUIActionAsync {...props} />;
		},
	},
	[ActionKeys.JsmCreateRequest]: {
		renderer: (props) => {
			di(FlagsProvider, JsmCreateRequestAction);
			return fg('service_agent_jsm_request_custom_ui') ? (
				<JsmCreateRequestAction {...props} />
			) : undefined;
		},
	},
	[ActionKeys.JsmCreateRequestV2]: {
		renderer: JsmCreateRequestAction,
	},
	[ActionKeys.CreateRequest]: {
		renderer: JsmCreateRequestAction,
	},
	[ActionKeys.JsmCreateEmbeddedRequest]: {
		renderer: JsmCreateEmbeddedRequestAction,
	},
	[ActionKeys.JsmSolutionComposerPlanSummary]: {
		renderer: JsmSolutionComposerPlanAction,
	},
	[ActionKeys.JsmSuggestAndAddFieldsToRequestType]: {
		renderer: JsmUpdateRequestTypeAction,
	},
	[ActionKeys.JsmAtlassianProjectCreateJsmRequestTypeWithFields]: {
		renderer: JsmCreateRequestTypeAction,
	},
	[ActionKeys.JsmAtlassianProjectCreateJsmQueue]: {
		renderer: JsmCreateQueueAction,
	},
	[ActionKeys.JsmAtlassianProjectCreateJsmQueueGroup]: {
		renderer: JsmCreateQueueGroupAction,
	},
	[ActionKeys.JsmAtlassianProjectUpdateJsmQueue]: {
		renderer: JsmUpdateQueueAction,
	},
	[ActionKeys.JsmAtlassianProjectUpdateJsmQueueGroup]: {
		renderer: JsmUpdateQueueGroupAction,
	},
	[ActionKeys.JsmAtlassianProjectUpdateRequestTypePortalGroups]: {
		renderer: JsmUpdateRequestTypePortalGroupsAction,
	},
	[ActionKeys.JsmAtlassianRequestTypeUpdateWorkflow]: {
		renderer: JsmUpdateRequestTypeWorkflowAction,
	},
	[ActionKeys.JsmSelectHardwareTypes]: {
		condition: () => fg('itam_ftw'),
		renderer: (props) => {
			di(FlagsProvider, JsmSelectHardwareTypesAction);
			return <JsmSelectHardwareTypesAction {...props} />;
		},
	},
	[ActionKeys.JsmSelectHardwareTypesSkill]: {
		condition: () => fg('itam_ftw'),
		renderer: (props) => {
			di(FlagsProvider, JsmSelectHardwareTypesAction);
			return <JsmSelectHardwareTypesAction {...props} />;
		},
	},
	[ActionKeys.HamOnboardingSetupComplete]: hamOnboardingSetupCompleteEntry,
	[ActionKeys.HamOnboardingSetupCompleteSkill]: hamOnboardingSetupCompleteEntry,
	// Deep Research Actions
	[ActionKeys.DeepResearchCreatePageAction]: {
		renderer: DeepResearchActions,
	},
	// Studio Actions
	[ActionKeys.StudioAutomationTriggerRuleBuild]: {
		renderer: (props) => {
			di(StudioAutomationTriggerRuleBuildAction);
			return <StudioAutomationTriggerRuleBuildAction {...props} />;
		},
	},
	[ActionKeys.StudioAutomationCreateRule]: {
		renderer: (props) => {
			di(StudioAutomationCreateRuleAction);
			return <StudioAutomationCreateRuleAction {...props} />;
		},
	},
	[ActionKeys.StudioTriggerSolutionPlan]: {
		renderer: StudioTriggerSolutionBuildAction,
	},
	[ActionKeys.RequireInformationTool]: {
		renderer: RequireInformationUIAction,
	},
	[ActionKeys.HiringManagerTool]: {
		renderer: (props) => {
			di(FlagsProvider);
			return fg('hiring_manager_action_button_choices_enabled') ? (
				<HiringManagerToolUIAction {...props} />
			) : null;
		},
	},
	[ActionKeys.StudioUpdateAgentDraft]: {
		renderer: StudioUpdateAgentDraftAction,
	},
	[ActionKeys.UpdateAgentConfigurationTool]: {
		renderer: UpdateAgentConfigurationAction,
	},
	[ActionKeys.StudioWorkspaceUpdateAgentDraft]: {
		renderer: StudioWorkspaceUpdateAgentDraftAction,
	},
	[ActionKeys.StudioCreateAgent]: {
		renderer: (props) => <StudioCreateAgentRenderer {...props} />,
	},
	[ActionKeys.StoreRecommendationCard]: {
		renderer: (props) => {
			di(FlagsProvider);
			return fg('rovo_chat_store_recommendation_action') ? (
				<StoreRecommendationAction {...props} />
			) : undefined;
		},
	},
	[ActionKeys.StudioLandingPageRedirect]: {
		renderer: (props) => {
			di(StudioLandingPageRedirect);
			return <StudioLandingPageRedirect {...props} />;
		},
	},
	[ActionKeys.AtlassianTeamUpdateArchive]: {
		renderer: (props) => {
			di(FlagsProvider);
			return fg('enable_clean_up_teams_agentic_skill') ? (
				<AtlassianTeamUpdateArchiveAction {...props} />
			) : undefined;
		},
		getAdoptedObjects: (action) => [{ objectType: 'team', objectId: action.data.teamId }],
	},
	[ActionKeys.FocusCreateAsk]: {
		renderer: (props) => {
			di(FlagsProvider, FocusAskCreateAction);
			if (!fg('display_components_for_create-focus-ask_skill') || !props.orgId) {
				return undefined;
			}
			return <FocusAskCreateAction {...props} orgId={props.orgId} />;
		},
	},
	[ActionKeys.ScheduledChat]: {
		renderer: (props) =>
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			FeatureGates.getExperimentValue('rovo_chat_scheduled_chat', 'isEnabled', false) ? (
				<ScheduledChatAction {...props} />
			) : undefined,
	},
	[ActionKeys.MultipleChoiceQuestionsTool]: {
		renderer: (props) =>
			fg('jira-ai-agentic-planning-technical-changes') ? (
				<MultipleChoiceQuestionsToolInChat
					{...props}
					AskQuestionCompletedView={AskQuestionCompletedView}
				/>
			) : undefined,
	},
	[ActionKeys.AgenticPlanTool]: {
		renderer: (props) =>
			fg('jira-ai-agentic-planning-technical-changes') ? (
				<AgenticPlanToolInChat {...props} />
			) : undefined,
	},
	// Convo AI agent tools
	[ActionKeys.ExecutePython]: {
		renderer: ExecutePythonRendererAsync,
	},
	[ActionKeys.PlanComplete]: {
		renderer: PlanCompleteRendererAsync,
	},
	[ActionKeys.AskQuestion]: {
		renderer: AskQuestionRendererAsync,
	},
	[ActionKeys.ImageSearch]: {
		renderer: ImageSearchActionAsync,
	},
	// Bitbucket
	[ActionKeys.BitbucketPullRequestComment]: {
		renderer: undefined,
		getAdoptedObjects: (action) => [
			{
				objectType: 'pull-request',
				objectId: action.data.pullRequestId,
			},
		],
	},
	[ActionKeys.CreateKBCard]: {
		renderer: CreateKBCard,
	},
	[ActionKeys.CreateKbArticlesCard]: {
		renderer: CreateKBCard,
	},
	[ActionKeys.CreateCustomSkillTool]: {
		renderer: (props) => {
			if (!fg('studio_custom_skills_m1_frontend')) return undefined;
			return <StudioCustomSkillRenderer {...props} />;
		},
	},
	[ActionKeys.UpdateCustomSkillTool]: {
		renderer: (props) => {
			if (!fg('studio_custom_skills_m1_frontend')) return undefined;
			return <StudioCustomSkillRenderer {...props} />;
		},
	},
};

const jiraPlansRendererEntries = [
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_update_work_sources',
		legacyKey: ActionKeys.JiraPlansUpdateWorkSource,
		registryEntry: { renderer: UpdateWorkSourceAction },
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_add_program_board',
		legacyKey: ActionKeys.JiraPlansCreateProgramBoard,
		registryEntry: { renderer: AddProgramBoardAction },
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_delete_program_board',
		legacyKey: ActionKeys.JiraPlansDeleteProgramBoard,
		registryEntry: { renderer: DeleteProgramBoardAction },
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_create_work_item',
		legacyKey: ActionKeys.JiraPlansCreateWorkItem,
		registryEntry: { renderer: CreateWorkItemAction },
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_create_release',
		legacyKey: ActionKeys.JiraPlansCreateRelease,
		registryEntry: { renderer: CreateReleaseAction },
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_set_release_enabled',
		legacyKey: ActionKeys.JiraPlansManageFeatures,
		registryEntry: { renderer: ManageFeaturesAction },
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_add_work_item_dependencies',
		legacyKey: ActionKeys.JiraPlansAddDependency,
		registryEntry: { renderer: AddDependencyAction },
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_remove_work_item_dependencies',
		legacyKey: ActionKeys.JiraPlansRemoveDependency,
		registryEntry: { renderer: RemoveDependencyAction },
	},
] as const;

const jiraPlansRegistry = Object.fromEntries(
	jiraPlansRendererEntries.flatMap(({ anthropicKey, legacyKey, registryEntry }) => [
		[
			anthropicKey,
			{
				...registryEntry,
				condition: () => fg('rovo_chat_jira_plans_anthropic_migration'),
			},
		],
		[
			anthropicKey.replace('execute_is.', 'is.'),
			{
				...registryEntry,
				condition: () => fg('rovo_chat_jira_plans_anthropic_migration'),
			},
		],
		[
			legacyKey,
			{
				...registryEntry,
				condition: () => !fg('rovo_chat_jira_plans_anthropic_migration'),
			},
		],
	]),
) as ActionRendererRegistry<ActionRendererKeyToPayload>;

const createPredicateRegistry = (
	getRovoToolsResumptionMigrationEnabled: GetRovoToolsResumptionMigrationEnabled,
): ActionRendererPredicateRegistry<ActionRendererPredicateKeyToPayload> => ({
	[ActionPredicateKeys.Resumption]: {
		predicate: (actions: MessageAction[]) =>
			actions.every(
				(action) =>
					Boolean(action.toolSource) &&
					!(
						action.key === ActionKeys.JiraCreateSprint ||
						action.key === ActionKeys.JiraStartSprint ||
						action.key === ActionKeys.JiraMoveToSprint ||
						(action.key === ActionKeys.GoogleDocCreate && !getRovoToolsResumptionMigrationEnabled())
					) &&
					!(
						jiraPlansActionData.isLegacyActionKey(action.key) ||
						jiraPlansActionData.isCustomUiActionKey(action.key)
					) &&
					!(
						action.key === ActionKeys.ConfluenceFolderCreate &&
						fg('confluence_folder_create_action_custom_ui')
					),
			),
		renderer: ResumptionRendererAsync,
	},
});

const defaultGetActionRenderer = {
	ActionRenderer: undefined,
	ActionRendererFallback: undefined,
};

function logAndFallback(result: ActionValidationFailure, action: MessageAction) {
	logException({
		exception: result.error,
		name: 'conversation-assistant.useActionRenderer.getActionRenderer.validation-failed',
		tags: {
			component: 'useActionRenderer',
			actionKey: action.key,
			invocationId: action.invocationId,
			actionId: String(action.actionId),
			status: String(action.status),
		},
	});
	return defaultGetActionRenderer;
}

const liveChatRegistry: ActionRendererRegistry<ActionRendererKeyToPayload> = {
	[ActionKeys.JsmLivechat]: {
		renderer: JsmLivechatAction,
	},
};

export const useActionRenderer = (
	getRovoToolsResumptionMigrationEnabled: GetRovoToolsResumptionMigrationEnabled = defaultGetRovoToolsResumptionMigrationEnabled,
): {
	getActionRenderer: GetOpaqueActionRenderer;
} => {
	const registry = useMemo<ActionRendererRegistry<ActionRendererKeyToPayload>>(
		() => ({
			...jiraGatedRegistery,
			...unifiedRegistery,
			...createRovoToolsResumptionMigrationRegistry(getRovoToolsResumptionMigrationEnabled),
			...jiraPlansRegistry,
			...liveChatRegistry,
		}),
		[getRovoToolsResumptionMigrationEnabled],
	);

	const predicateRegistry = useMemo(
		() => createPredicateRegistry(getRovoToolsResumptionMigrationEnabled),
		[getRovoToolsResumptionMigrationEnabled],
	);

	const { getActionRenderer: getActionRendererWithRegistry } = useActionRendererWithRegistry(
		registry,
		predicateRegistry,
	);

	const getActionRenderer: GetOpaqueActionRenderer = useCallback(
		(actionKey: string, actions: MessageAction[]) => {
			try {
				return getActionRendererWithRegistry(actionKey, actions);
			} catch (error) {
				if (isActionValidationFailureError(error)) {
					return logAndFallback(error.validationFailure, error.action);
				}
				return defaultGetActionRenderer;
			}
		},
		[getActionRendererWithRegistry],
	);

	return { getActionRenderer };
};
